import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Heading, HStack, Input, Text } from '@chakra-ui/react';
import { SketchMathDocument, createSketchMathSession, startSketchMathFileJob, getSketchMathFileJob } from '../../services/sketchmath';

type Props = {document: SketchMathDocument|null; onClose:()=>void};
export default function SketchMathFiles({document:current,onClose}:Props){
  const [items,setItems]=useState<any[]>([]),[query,setQuery]=useState(''),[busy,setBusy]=useState(false),[error,setError]=useState('');
  const epoch=useRef(0),input=useRef<HTMLInputElement>(null);
  const run=async(action:string,document?:unknown)=>{
    const token=++epoch.current;setBusy(true);setError('');
    try{
      const job=await startSketchMathFileJob({action,document});
      const poll=async()=>{
        if(token!==epoch.current)return;
        try{
          const state=await getSketchMathFileJob(job.job_id);
          if(token!==epoch.current)return;
          if(state.state==='RUNNING'){window.setTimeout(poll,500);return;}
          setBusy(false);
          if(state.state==='FAILED'){setError(state.error.message);return;}
          if(action==='list')setItems(state.result.items);
          else window.location.assign(`/tools/sketchmath?session=${encodeURIComponent(state.result.session_id)}`);
        }catch(e){if(token===epoch.current){setBusy(false);setError(String(e));}}
      };void poll();
    }catch(e){setBusy(false);setError(String(e));}
  };
  useEffect(()=>{const generation=epoch;void run('list');return()=>{generation.current++;};},[]); // Catalog work remains a server job if this panel closes.
  const download=()=>{
    if(!current)return;
    const blob=new Blob([JSON.stringify(current,null,2)],{type:'application/json'});
    const url=URL.createObjectURL(blob),a=window.document.createElement('a');
    a.href=url;a.download=`${current.document_id}.sketchmath.json`;a.click();window.setTimeout(()=>URL.revokeObjectURL(url),1000);
  };
  const importFile=async(file?:File)=>{
    if(!file)return;
    try{
      if(file.size>8*1024*1024)throw new Error('Import supports editable SketchMath JSON up to 8 MiB.');
      if(!file.name.toLowerCase().endsWith('.json'))throw new Error('This importer supports editable SketchMath JSON. STEP/STL reference import is not yet supported.');
      const value=JSON.parse(await file.text());await run('import',value.document||value);
    }catch(e){setError(String(e));}
    if(input.current)input.current.value='';
  };
  const create=async()=>{setBusy(true);try{const s=await createSketchMathSession();window.location.assign(`/tools/sketchmath?session=${encodeURIComponent(s.session_id)}`);}catch(e){setError(String(e));setBusy(false);}};
  return <Box role="dialog" aria-modal="true" aria-label="SketchMath files" position="fixed" inset={6} zIndex={2000} bg="var(--chakra-colors-chakra-body-bg)" borderWidth={1} borderRadius="lg" p={5} overflowY="auto" boxShadow="2xl">
    <HStack justify="space-between"><Heading size="md">Your designs</Heading><Button onClick={onClose}>Close files</Button></HStack>
    <Text my={3}>Open a saved design or import an editable SketchMath document. Designs save when you apply a change.</Text>
    <HStack wrap="wrap" mb={3}>
      <Button onClick={create} isDisabled={busy}>New design</Button>
      <Button onClick={()=>input.current?.click()} isDisabled={busy}>Import design</Button>
      <Button onClick={download} isDisabled={!current}>Download editable JSON</Button>
      <Button onClick={()=>void run('list')} isDisabled={busy}>Refresh files</Button>
    </HStack>
    <input ref={input} type="file" accept=".json,application/json" aria-label="Import editable SketchMath JSON" style={{display:'none'}} onChange={e=>void importFile(e.target.files?.[0])}/>
    <Text fontSize="sm">Import: SketchMath JSON, up to 8 MiB. Parameters and constraints are retained; imported copies start a new undo history. STEP/STL exports are available in Advanced / Export.</Text>
    <Input my={3} aria-label="Find saved design" placeholder="Search names or design IDs" value={query} onChange={e=>setQuery(e.target.value)}/>
    {busy&&<Text role="status">Loading or validating designs…</Text>}
    {error&&<Text role="alert">{error}</Text>}
    {!busy&&!error&&!items.length&&<Text>No saved designs yet.</Text>}
    {items.filter(i=>(i.name+' '+i.session_id).toLowerCase().includes(query.toLowerCase())).map(i=><Box key={i.session_id} borderBottomWidth={1} py={3}>
      <HStack justify="space-between"><Box><Text fontWeight="bold">{i.name}</Text><Text fontSize="sm">{i.session_id} · Revision {i.revision??'?'} · {i.feature_count??'?'} features</Text><Text fontSize="sm">{i.updated_at?new Date(i.updated_at).toLocaleString():''}</Text></Box>
      {!i.unavailable&&<Button as="a" href={`/tools/sketchmath?session=${encodeURIComponent(i.session_id)}`}>Open design</Button>}</HStack>
    </Box>)}
  </Box>;
}
